import { EVMChain } from '@repo/types-client';

import {
  baseContractAddresses,
  ethereumContractAddresses,
  externalBaseContractAddresses,
} from '../data';

/**
 * Represents the details of a blockchain contract
 * @interface ContractDetails
 * @property {EVMChain} chain - The blockchain network where the contract is deployed
 * @property {string} contractType - The type of contract (e.g., 'ERC721', 'ERC1155', 'ERC7401')
 */
export interface ContractDetails {
  chain: EVMChain;
  contractType: string;
}

/**
 * Maps contract addresses to their corresponding details
 * @typedef {Record<string, ContractDetails>} ContractMapping
 */
type ContractMapping = Record<string, ContractDetails>;

/**
 * Mapping of contract addresses to their details.
 * All addresses are stored in lowercase for case-insensitive lookups.
 */
export const contractDetailsMapping: ContractMapping = {
  [ethereumContractAddresses.doodles.toLowerCase()]: {
    chain: EVMChain.ethereum,
    contractType: 'ERC721',
  },
  [ethereumContractAddresses.doodlesDrops.toLowerCase()]: {
    chain: EVMChain.ethereum,
    contractType: 'ERC1155',
  },
  [ethereumContractAddresses.dooplicator.toLowerCase()]: {
    chain: EVMChain.ethereum,
    contractType: 'ERC721',
  },
  [ethereumContractAddresses.genesisBox.toLowerCase()]: {
    chain: EVMChain.ethereum,
    contractType: 'ERC721',
  },
  [ethereumContractAddresses.spaceDoodles.toLowerCase()]: {
    chain: EVMChain.ethereum,
    contractType: 'ERC721',
  },
  [baseContractAddresses.avatar.toLowerCase()]: {
    chain: EVMChain.base,
    contractType: 'ERC7401',
  },
  [baseContractAddresses.pack.toLowerCase()]: {
    chain: EVMChain.base,
    contractType: 'ERC1155',
  },
  [baseContractAddresses.pass.toLowerCase()]: {
    chain: EVMChain.base,
    contractType: 'ERC1155',
  },
  [baseContractAddresses.redeemable.toLowerCase()]: {
    chain: EVMChain.base,
    contractType: 'ERC1155',
  },
  [baseContractAddresses.wearable.toLowerCase()]: {
    chain: EVMChain.base,
    contractType: 'ERC7401',
  },
  [externalBaseContractAddresses.doodlesCertifiedViral.toLowerCase()]: {
    chain: EVMChain.external_base,
    contractType: 'ERC1155',
  },
  [externalBaseContractAddresses.doodlesComics.toLowerCase()]: {
    chain: EVMChain.external_base,
    contractType: 'ERC1155',
  },
};

/**
 * Retrieves contract details for a given contract address
 * @param {string} address - The contract address to look up
 * @returns {Promise<ContractDetails | null>} The contract details if found, null otherwise
 * @example
 * const details = await getContractDetailsByAddress('0x...');
 * if (details) {
 *   console.log(details.chain, details.contractType);
 * }
 */
export async function getContractDetailsByAddress(
  address: string,
): Promise<ContractDetails | null> {
  const contractDetails = contractDetailsMapping[address.toLowerCase()];
  if (!contractDetails) {
    console.warn(`No contract details found for address: ${address}`);
  }
  return contractDetails || null;
}
